import React from 'react';
import { useNavigate } from 'react-router-dom';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { useRoleAccess } from '../../../../hooks/useRoleAccess';
import { formatLongDate } from '../../utils/dashboardDates';
import { splitDay, getDurationMinutes } from '../../utils/daySchedule';
import DayProgressRing from './DayProgressRing';
import NowPanel from './NowPanel';
import DayTimeline from './DayTimeline';
import styles from './TodayHero.module.css';

function getGreeting(name) {
  const hour = new Date().getHours();
  const firstName = (name || '').split(' ')[0];
  const greeting = hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite';
  return firstName ? `${greeting}, ${firstName}` : greeting;
}

/**
 * O expediente acabou? Vale o fechamento do horário de funcionamento da
 * clínica (Preferências) ou o fim da última consulta, o que vier depois.
 * Sem horário configurado, usa 18h (a mesma janela mínima da linha do dia).
 */
function isAfterClosing(appointments, availableTime) {
  const configuredEnd = Array.isArray(availableTime) ? Number(availableTime[1]) : NaN;
  let closing = (Number.isNaN(configuredEnd) ? 18 : configuredEnd) * 60;
  appointments.forEach((a) => {
    const d = new Date(a.date);
    closing = Math.max(closing, d.getHours() * 60 + d.getMinutes() + getDurationMinutes(a.duration));
  });
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes() >= closing;
}

function getSummary(total, done, toConfirm, ownAgenda, dayOver) {
  if (total === 0) return ownAgenda ? 'Nenhuma consulta na sua agenda hoje.' : 'Nenhuma consulta na agenda de hoje.';
  if (done === total) {
    const realizadas = done === 1 ? 'a consulta foi realizada' : `as ${done} consultas foram realizadas`;
    // Antes do fechamento ainda pode entrar encaixe: o dia só "encerra" depois do expediente
    return dayOver ? `Dia encerrado: ${realizadas}.` : `Sem mais atendimentos hoje: ${realizadas}.`;
  }
  const consultas = total === 1 ? '1 consulta' : `${total} consultas`;
  // Com o alcance "Só os meus pacientes" a lista já vem só com a agenda da pessoa
  const opener = ownAgenda ? `Hoje você tem ${consultas}` : `Hoje ${total === 1 ? 'tem' : 'são'} ${consultas}`;
  if (toConfirm === 0) return `${opener}, todas confirmadas.`;
  return `${opener}. ${toConfirm === 1 ? '1 ainda sem confirmação' : `${toConfirm} ainda sem confirmação`}.`;
}

/**
 * Topo do Início: o resumo do dia com o gráfico (mesma coluna de "Para
 * resolver") e, ao lado, quem está na clínica agora.
 */
function TodayHero({ appointments, onStepChecksChange, isMine = false, ownResourceId = null, viewToggle = null }) {
  const navigate = useNavigate();
  const { UserData, CompanyConfig } = DataProviderInstance();
  // Quem acessa a tela de atendimento: a marcação "Profissional de saúde", não o papel
  const { canAccessHealthData, hasOwnScheduleScope } = useRoleAccess();
  const canAttend = canAccessHealthData();
  // "Só os meus pacientes" ou o filtro "Minha agenda": a lista já vem só com a agenda da pessoa
  const ownAgenda = hasOwnScheduleScope() || isMine;

  const total = appointments.length;
  const done = appointments.filter((a) => a.status === 'completed').length;
  const toConfirm = appointments.filter((a) => a.status === 'scheduled').length;
  const { live, upcoming } = splitDay(appointments);
  // Com mais de um profissional no dia, mostra de quem é cada consulta
  const showResource = new Set(appointments.map((a) => a.resource ?? 'none')).size > 1;
  const longDate = formatLongDate();

  return (
    <div className={styles.layout}>
      <section className={styles.hero} aria-label="Resumo do dia">
        <div className={styles.intro}>
          <div className={styles.dateRow}>
            <span className={styles.date}>{longDate.charAt(0).toUpperCase() + longDate.slice(1)}</span>
            {viewToggle}
          </div>
          <h1 className={styles.greeting}>{getGreeting(UserData?.profile?.name)}</h1>
          <span className={styles.accent} aria-hidden="true" />

          <div className={styles.summaryRow}>
            {total > 0 && <DayProgressRing done={done} total={total} />}
            <div className={styles.summaryText}>
              <p className={styles.summary}>{getSummary(total, done, toConfirm, ownAgenda, isAfterClosing(appointments, CompanyConfig?.AvailableTime))}</p>
              {total > 0 && (
                <span className={styles.summaryHint}>{done} de {total} {total === 1 ? 'realizada' : 'realizadas'}</span>
              )}
            </div>
          </div>

          <div className={styles.buttons}>
            <button type="button" className={styles.primaryButton} onClick={() => navigate('/schedule/appointments')}>
              <CalendarMonthOutlinedIcon style={{ fontSize: 18 }} />
              Abrir agenda
            </button>
            <button type="button" className={styles.secondaryButton} onClick={() => navigate('/patients')}>
              <PeopleOutlineIcon style={{ fontSize: 18 }} />
              Pacientes
            </button>
          </div>
        </div>

        {total > 0 && <DayTimeline appointments={appointments} showResource={showResource} canAttend={canAttend} ownResourceId={ownResourceId} />}
      </section>

      <NowPanel
        live={live}
        upcoming={upcoming}
        hasAppointments={total > 0}
        showResource={showResource}
        canAttend={canAttend}
        onStepChecksChange={onStepChecksChange}
      />
    </div>
  );
}

export default TodayHero;
